import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import TypeaheadLabelTooltip from './labelTooltip';

/**
 * @param {string} id
 * @param {string} query
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} onQueryChange
 * @param {() => void} onFocus
 * @param {string} [label]
 * @param {string} [placeholderText]
 * @param {boolean} isDropdownOpen
 * @param {Category[]} filteredItems
 * @param {(item: item) => void} onItemClick
 * @param {(e: React.FocusEvent<HTMLInputElement>) => void} onBlur
 * @param {(e: React.KeyboardEvent<HTMLInputElement>) => void} onKeyDown
 * @param {string} [highlightedItemValue]
 * @param {string} [tooltipText]
 * @param {string} [tooltipLinkText]
 * @param {boolean}  [rightAlignedTooltip]
 * @returns {JSX.Element}
 */
const TypeaheadInput = ({
  id,
  query,
  onQueryChange,
  onFocus,
  label,
  placeholderText,
  isDropdownOpen,
  filteredItems,
  onItemClick,
  tooltipText,
  tooltipLinkText,
  onBlur,
  onKeyDown,
  highlightedItemValue,
  translations,
  inputRef,
  rightAlignedTooltip = true,
}) => {
  return (
    <div className="c-typeahead">
      {label && (
        <div
          className="c-typeahead__label"
          style={{ justifyContent: rightAlignedTooltip ? 'space-between' : '' }}
        >
          <label htmlFor={id}>{label}</label>
          {tooltipText && tooltipLinkText && (
            <TypeaheadLabelTooltip
              id={id}
              tooltipLinkText={tooltipLinkText}
              tooltipText={tooltipText}
              translations={translations}
              isRightAligned={rightAlignedTooltip}
            />
          )}
        </div>
      )}

      <div className="c-typeahead__input-container">
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          value={query}
          onChange={onQueryChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          onClick={onFocus}
          placeholder={placeholderText}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isDropdownOpen}
          aria-controls={`${id}-listbox`}
          aria-activedescendant={
            highlightedItemValue
              ? `typeahead-option-${highlightedItemValue}`
              : undefined
          }
        />
        <div
          className="c-typeahead__input-toggle"
          aria-hidden
          onMouseDown={(e) => e.preventDefault()}
          onClick={isDropdownOpen ? onBlur : onFocus}
        />
        <div
          className={classNames({
            'c-typeahead__chevron': true,
            'c-typeahead__chevron--is-expanded': isDropdownOpen,
          })}
        />

        {isDropdownOpen && filteredItems.length > 0 && (
          <div className="c-typeahead__dropdown">
            <div className="c-typeahead__dropdown__content">
              {filteredItems.map((category) => (
                <div key={category.category} className="c-typeahead__category">
                  {category.category !== '' && (
                    <span className="c-typeahead__category__label">
                      {category.category}
                    </span>
                  )}
                  <ul
                    id={`${id}-listbox`}
                    role="listbox"
                    className="c-typeahead__list"
                  >
                    {category.items.map((item) => (
                      <li
                        id={`typeahead-option-${item.value}`}
                        key={item.value}
                        role="option"
                        aria-selected={item.value === highlightedItemValue}
                        className={classNames('c-typeahead__item', {
                          'c-typeahead__item--highlighted':
                            item.value === highlightedItemValue,
                        })}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onItemClick(item)}
                      >
                        <span>{item.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

TypeaheadInput.propTypes = {
  id: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholderText: PropTypes.string,
  isDropdownOpen: PropTypes.bool.isRequired,
  filteredItems: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  onItemClick: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  highlightedItemValue: PropTypes.string,
  tooltipTitle: PropTypes.string,
  tooltipText: PropTypes.string,
  tooltipLinkText: PropTypes.string,
  rightAlignedTooltip: PropTypes.bool,
};

export default TypeaheadInput;
